import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Car, Truck, HardHat, Boat, House, MapPin, Tag, CheckCircle, Play, FirstAidKit } from "@phosphor-icons/react";

const ICONS = { auto: Car, trailer: Truck, construction: HardHat, boat: Boat, realestate: House, medical: FirstAidKit };
export const INDUSTRY_KEY = "orbit_industry";

export function IndustryLogo({ industryKey, label, className = "h-11 w-11" }) {
  return (
    <img src={`/industry/${industryKey}.jpg`} alt={`${label} example with QR label`}
      className={`${className} shrink-0 rounded-lg object-cover border border-zinc-200 bg-zinc-100`} data-testid={`industry-logo-${industryKey}`} />
  );
}

export function useIndustries() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(() => localStorage.getItem(INDUSTRY_KEY) || "");
  useEffect(() => { api.get("/industries").then(({ data }) => setList(data)).catch(() => {}); }, []);
  const select = (k) => { setSelected(k); if (k) localStorage.setItem(INDUSTRY_KEY, k); else localStorage.removeItem(INDUSTRY_KEY); };
  return { list, selected, select, current: list.find((i) => i.key === selected) };
}

export function IndustryBubbleBar({ list, selected, onSelect, compact = false }) {
  return (
    <div className={`flex gap-2 ${compact ? "overflow-x-auto pb-2 snap-x min-w-0 max-w-full [scrollbar-width:thin]" : "flex-wrap"}`} data-testid="industry-bubbles">
      {list.map((i) => {
        const Icon = ICONS[i.key] || Tag;
        const on = selected === i.key;
        return (
          <button key={i.key} type="button" onClick={() => onSelect(on ? "" : i.key)} aria-pressed={on}
            className={`shrink-0 snap-start inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${on ? "bg-zinc-900 border-zinc-900 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}
            data-testid={`industry-bubble-${i.key}`}>
            <Icon size={16} weight={on ? "fill" : "duotone"} />{i.label}
          </button>
        );
      })}
    </div>
  );
}

export function IndustryPanel({ industry, showCta = true }) {
  if (!industry) return null;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid={`industry-panel-${industry.key}`}>
      <div className="flex gap-4">
        <IndustryLogo industryKey={industry.key} label={industry.label} className="h-14 w-14" />
        <div>
          <h3 className="font-display font-bold text-zinc-900 text-lg">{industry.label}</h3>
          <p className="text-sm text-zinc-600 leading-relaxed mt-0.5">{industry.desc}</p>
          <p className="text-xs font-semibold text-zinc-400 mt-1.5">{industry.tags}</p>
        </div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-500 mb-2">Inventory types</p>
          <div className="space-y-2.5">
            {industry.asset_types.map((t) => (
              <div key={t.key} className="rounded-lg bg-zinc-50 border border-zinc-100 p-2.5" data-testid={`industry-type-${t.key}`}>
                <p className="text-sm font-semibold text-zinc-900">{t.label}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{t.fields.map((f) => f.label).join(" · ")}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-500 mb-2">Locations we set up for you</p>
            <div className="flex flex-wrap gap-1.5">
              {industry.locations.map((l) => <span key={l} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-100 px-2 py-0.5 text-xs font-medium"><MapPin size={11} weight="fill" />{l}</span>)}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-500 mb-2">Built-in</p>
            <ul className="space-y-1">
              {industry.highlights.map((h) => <li key={h} className="flex items-start gap-1.5 text-sm text-zinc-700"><CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />{h}</li>)}
            </ul>
          </div>
        </div>
      </div>
      {showCta && (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link to="/register" className="inline-flex items-center rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-5 py-2.5 transition-colors" data-testid="industry-start-free">Start free as {industry.label}</Link>
          <Link to={`/videos?cat=${encodeURIComponent(industry.video_category)}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-700 hover:text-zinc-900" data-testid="industry-watch-video"><Play size={16} weight="fill" />Watch the demo</Link>
        </div>
      )}
    </div>
  );
}
